import {
  Directive,
  inject,
  input,
  TemplateRef,
  ViewContainerRef,
  effect,
} from "@angular/core";
import {
  AuthorizationService,
  Permission,
} from "../services/authorization.service";

/**
 * Structural directive to show/hide elements based on permissions
 *
 * Usage:
 * ```html
 * <button *appHasPermission="'CREATE_SUBTASK'">Create Subtask</button>
 * <div *appHasPermission="'VIEW_ALL_SUBTASKS'">Admin content</div>
 * ```
 *
 * The directive will automatically update when user permissions change
 */
@Directive({
  selector: "[appHasPermission]",
  standalone: true,
})
export class HasPermissionDirective {
  private readonly _authz = inject(AuthorizationService);
  private readonly _templateRef = inject(TemplateRef<unknown>);
  private readonly _viewContainer = inject(ViewContainerRef);

  // Use input signal for reactivity
  readonly appHasPermission = input.required<Permission>();

  private _hasView = false;

  constructor() {
    // Effect runs whenever permission input or user permissions change
    effect(() => {
      const permission = this.appHasPermission();
      const hasPermission = this._authz.hasPermission(permission);
      this._updateView(hasPermission);
    });
  }

  private _updateView(hasPermission: boolean): void {
    if (hasPermission && !this._hasView) {
      this._viewContainer.createEmbeddedView(this._templateRef);
      this._hasView = true;
    } else if (!hasPermission && this._hasView) {
      this._viewContainer.clear();
      this._hasView = false;
    }
  }
}
